import { AfterViewInit, Directive, ElementRef, HostListener, Input, OnDestroy } from "@angular/core";
@Directive({
  selector: "[footerLabelWidth]"
})
export class FooterLabelWidthDirective implements AfterViewInit, OnDestroy {
  @Input() buttonCount: number;

  private static readonly DEFAULT_RATIO = 1.55;
  private static readonly FOOTER_PADDING = 48;
  private static readonly MIN_FONT_SIZE = 9;
  private static readonly MAX_FONT_SIZE = 16;
  private static readonly MOBILE_BREAKPOINT = 786;
  private static readonly RESIZE_DEBOUNCE_MS = 160;
  private static readonly RATIO_STEP = 0.1;

  private resizeTimeout: number | null = null;

  constructor(private readonly el: ElementRef<HTMLElement>) { }

  ngAfterViewInit(): void {
    this.checkLabelWidth();
  }

  ngOnDestroy(): void {
    this.clearResizeTimeout();
  }

  @HostListener("window:resize")
  onResize(): void {
    this.checkLabelWidth();
  }

  /** Applies label width immediately and schedules a debounced recheck */
  private checkLabelWidth(): void {
    this.applyLabelWidth();
    this.clearResizeTimeout();

    // Re-run after CSS variable updates (e.g., --vh changes on orientation)
    this.resizeTimeout = window.setTimeout(() => {
      this.applyLabelWidth();
      this.resizeTimeout = null;
    }, FooterLabelWidthDirective.RESIZE_DEBOUNCE_MS);
  }

  /** Calculates and applies appropriate font size based on available space */
  private applyLabelWidth(): void {
    const labelElement = this.getLabelElement();
    if (!labelElement) {
      return;
    }

    const deviceWidth = document.documentElement.clientWidth;

    if (deviceWidth < FooterLabelWidthDirective.MOBILE_BREAKPOINT) {
      const maxWidth = this.calculateMaxWidth(deviceWidth);
      const vh = this.getVh();
      this.adjustFontSize(labelElement, maxWidth, vh);
    } else {
      labelElement.style.fontSize = "";
    }
  }

  /** Gets the label element from the DOM */
  private getLabelElement(): HTMLElement | null {
    return this.el.nativeElement.querySelector(".mat-button-wrapper > span");
  }

  /** Calculates maximum allowed width for the button label */
  private calculateMaxWidth(deviceWidth: number): number {
    const buttonWidth = this.el.nativeElement.clientWidth;
    const totalButtonWidth = (this.buttonCount + 1) * buttonWidth;
    const availableSpace = deviceWidth - FooterLabelWidthDirective.FOOTER_PADDING - totalButtonWidth;
    const buttonSpace = availableSpace / this.buttonCount;
    const allowedSpace = Math.min(buttonSpace, FooterLabelWidthDirective.FOOTER_PADDING);

    return buttonWidth + allowedSpace;
  }

  /** Reads the --vh CSS custom property with fallback to 1 */
  private getVh(): number {
    const rawVh = getComputedStyle(document.documentElement)
      .getPropertyValue("--vh")
      .replace("px", "")
      .trim();

    const vh = Number(rawVh);
    return Number.isFinite(vh) && vh > 0 ? vh : 1;
  }

  /** Recursively adjusts font size to fit within max width */
  private adjustFontSize(element: HTMLElement, maxWidth: number, vh: number, ratio = FooterLabelWidthDirective.DEFAULT_RATIO, iterations = 0): void {
    const clampedRatio = this.clampRatio(ratio, vh);
    element.style.fontSize = `${vh * clampedRatio}px`;

    const isTooWide = element.clientWidth > maxWidth;
    const minFontSize = FooterLabelWidthDirective.MIN_FONT_SIZE;
    const canReduceMore = (vh * clampedRatio) > minFontSize;

    if (isTooWide && canReduceMore) {
      this.adjustFontSize(element, maxWidth, vh, clampedRatio - FooterLabelWidthDirective.RATIO_STEP, iterations + 1);
    } else if (!isTooWide && iterations === 0) {
      // Attempt slight increase on first iteration
      const increasedRatio = this.clampRatio(clampedRatio + FooterLabelWidthDirective.RATIO_STEP, vh);
      element.style.fontSize = `${vh * increasedRatio}px`;
    }
  }

  /** Clamps ratio to respect MIN and MAX font size constraints */
  private clampRatio(ratio: number, vh: number): number {
    const fontSize = vh * ratio;
    if (fontSize > FooterLabelWidthDirective.MAX_FONT_SIZE) {
      return FooterLabelWidthDirective.MAX_FONT_SIZE / vh;
    }
    return ratio;
  }

  /** Clears the resize timeout to prevent memory leaks */
  private clearResizeTimeout(): void {
    if (this.resizeTimeout !== null) {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = null;
    }
  }
}
